import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BuildingIcon,
  CalendarIcon,
  HammerIcon,
  LayersIcon,
  LifeBuoyIcon,
  LightbulbIcon,
  MergeIcon,
  PlusIcon,
  RocketIcon,
  SearchXIcon,
  ThumbsDownIcon,
  XIcon,
} from 'lucide-react'

import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'

import { Button, Card, Input, Skeleton } from '@repo/ui'
import {
  ActionDropdown,
  CompanyAvatar,
  EmptyState,
  Header,
  QueryErrorState,
  SelectField,
  StatusChip,
  SupportTypeBadge,
  SystemGeneratedBadge,
  UserAvatarGroup,
} from 'components'
import { NARROW_COLUMN_WIDTH } from 'config'
import { useSnackbar } from 'contexts'
import { useReferrerState } from 'hooks'
import {
  useInternalBoardItems,
  useInternalCreateTheme,
  useInternalMergeBoardItems,
  useInternalSetBoardStatus,
  useInternalSetItemTheme,
  useInternalSupportRequests,
} from 'hooks/api'
import {
  BOARD_ITEM_STATUSES,
  SUPPORT_REPORT_SOURCES,
  SUPPORT_REQUEST_STATUSES_INTERNAL,
} from 'constants/statuses'
import { ROUTES } from 'routes'
import { relativeOrDateTime } from 'utils'
import {
  errorRefsOf,
  lastActivityAt,
  linkedAutoReportOf,
  supportReportSource,
} from 'utils/supportReportSource'
import type { BoardItemInternal, SupportQueueScope, SupportRequest } from 'services/supportApi'

import {
  applyInternalFilters,
  EMPTY_INTERNAL_FILTERS,
  InternalSupportListControls,
  type InternalListFilters,
} from './InternalSupportListControls'
import { useRequestActions } from './requestActions'

/**
 * The cross-company support queue (VRG-226 — C4 + C7 curation).
 *
 * **Replies happen here, not in Slack.** Slack (C5) is an outbound ping so
 * somebody notices a new request; the conversation itself lives in Vera, where the
 * request's full auto-captured context — page, browser, company, prior thread — is
 * already on screen. Answering from a Slack thread would mean answering with less
 * information than the app has.
 *
 * This page is the QUEUE. A request opens on its own page
 * (`InternalSupportRequestPage`, `/internal/support/:id`), which renders the same
 * `RequestThread` the customer's `/support/:id` does — see that page for why the
 * inline accordion this replaced was the wrong shape.
 */

/**
 * The glyph for each board status a staffer can set.
 *
 * One per VERB, chosen to match what the status chip beside it already says:
 *
 *   - **Planned** (blue) → calendar. The item is committed to and scheduled; a
 *     calendar is the conventional roadmap mark for exactly that.
 *   - **Building** (brand purple) → hammer. Work is happening. Deliberately NOT the
 *     clock used by a REQUEST's "Mark in progress": the two share the raw value
 *     `in_progress` while meaning different things by it — "someone is answering
 *     your ticket" versus "we are building the feature" — and that collision is the
 *     one this whole surface keeps having to disambiguate. Different meaning,
 *     different glyph.
 *   - **Shipped** (green) → rocket. Released. Not the truck or the package-check
 *     used elsewhere in this console, which mean physically shipping tags to a
 *     brand — reusing them here would put fulfilment's vocabulary on a software
 *     release inside the same admin surface.
 *
 * Held next to the status map rather than inline so the three are chosen as a SET
 * and stay distinguishable from each other, which is the property that was missing.
 */
const BOARD_STATUS_ICONS: Record<'planned' | 'in_progress' | 'shipped', React.ReactNode> = {
  planned: <CalendarIcon strokeWidth={1} />,
  in_progress: <HammerIcon strokeWidth={1} />,
  shipped: <RocketIcon strokeWidth={1} />,
}

/**
 * VRG-726 — the Source "column" of the queue, as one line on the card.
 *
 * This page is a card list, not a grid (see the cap note below for why), so the columns the
 * triage needs are a line rather than cells: the Source chip, then for anything automatic the
 * error ref and how often it happened, then what links it to a person. Every card carries the
 * chip, the same way a column has a value on every row; the rest appears only where it exists.
 *
 * The ref is shown, not copyable, here: the whole card is a link to the thread, and the thread
 * has the copy button and the "Find in logs" link (`ReportedErrorBlock`). A second control
 * inside a clickable card fights the card's own click.
 */
const SourceLine = ({ request }: { request: SupportRequest }) => {
  const source = supportReportSource(request)
  const { first, latest } = errorRefsOf(request)
  const linked = linkedAutoReportOf(request)
  const linkedCount = request.linkedUserReportCount ?? 0

  const facts: string[] = []
  if (request.isSystemGenerated) {
    const n = request.occurrenceCount ?? 1
    facts.push(n === 1 ? 'Seen once' : `Seen ${n} times`)
  }
  if (request.isSystemGenerated && linkedCount > 0) {
    facts.push(linkedCount === 1 ? '1 user report linked' : `${linkedCount} user reports linked`)
  }
  if (request.isSystemGenerated && request.userNoteAt) facts.push('User added a note')
  if (linked) facts.push(`Linked to automatic report #${linked.reference ?? '…'}`)

  return (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      sx={{ flexWrap: 'wrap', rowGap: 2 }}
      data-testid="support-queue-source"
    >
      <StatusChip statuses={SUPPORT_REPORT_SOURCES} status={source} size="small" />
      {first && (
        <Typography variant="body4" color="text.secondary">
          Ref{' '}
          <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.primary' }}>
            {first}
          </Box>
          {latest && (
            <>
              {' · latest '}
              <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.primary' }}>
                {latest}
              </Box>
            </>
          )}
        </Typography>
      )}
      {facts.length > 0 && (
        <Typography variant="body4" color="text.secondary">
          {facts.join(' · ')}
        </Typography>
      )}
    </Stack>
  )
}

export const InternalSupportPage = () => {
  const { open: notification } = useSnackbar()
  const theme = useTheme()
  const navigate = useNavigate()
  /*
    Handed to the request page so its `Header` renders "← Back to Support" and
    returns the staffer to THIS list rather than a reset one (§42). Derived here,
    at the link, rather than reconstructed at the destination — the destination
    cannot know which list, filtered how, the user came from.
  */
  const referrer = useReferrerState()

  const [tab, setTab] = useState<'requests' | 'board'>('requests')
  /*
    VRG-378. Which population of the queue is loaded, SERVER-side — not a client
    filter over already-loaded rows like `filters` below.

    That distinction is the reason it is separate state rather than another entry
    in `InternalListFilters`: the whole point is that Vera's own reports are never
    fetched by default, so they cannot inflate a count, appear in the derived
    `companyName` facet, or be revealed by clearing a filter. A client-side filter
    would leave them present-but-hidden, which is a different and weaker promise.
  */
  const [scope, setScope] = useState<SupportQueueScope>('brands')
  const [filters, setFilters] = useState<InternalListFilters>(EMPTY_INTERNAL_FILTERS)
  /*
    Just resets the state now. This used to also bump a `toolbarKey` to REMOUNT the whole
    toolbar, because the shared `Filters` drawer seeded its applied set with
    `useState(filters)` and never synced afterwards — so clearing this page's state left the
    drawer still showing Support selected with its count badge reading 1, a control claiming a
    filter the list was no longer applying. That was worked around here on the grounds that a
    sync effect in `Filters` would affect every list in the app; it now has one, guarded so it
    adopts a genuine parent change without echoing its own commits back or clobbering
    selections being staged in an open drawer. Remounting to move one piece of state also threw
    away the search box and sort menu beside it, which was never the intent.
  */
  const clearFilters = () => setFilters(EMPTY_INTERNAL_FILTERS)
  const [mergeSourceId, setMergeSourceId] = useState<string | null>(null)
  const [declineNote, setDeclineNote] = useState<{ id: string; note: string } | null>(null)
  /** The item whose theme picker is open, and the theme currently chosen in it. */
  const [themePicker, setThemePicker] = useState<{ id: string; themeItemId: string } | null>(
    null,
  )
  /** Open when creating a theme; `fromItemId` set when it was started from an item. */
  const [newTheme, setNewTheme] = useState<{ title: string; fromItemId?: string } | null>(null)

  const {
    data: requests = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useInternalSupportRequests(undefined, scope)
  const {
    data: boardItems = [],
    isLoading: boardLoading,
    isError: boardError,
    error: boardErrorObj,
    refetch: refetchBoard,
  } = useInternalBoardItems()

  const visibleRequests = useMemo(
    () => applyInternalFilters(requests, filters),
    [requests, filters],
  )

  /**
   * Everyone who asked for the thing a given request asks for.
   *
   * ## No API change was needed, and that was worth checking before assuming one
   *
   * `boardRequestCount` already ships on every request, but it is a COUNT and it is
   * scoped to the submitter's own company (`r2.company_id = ...` in `requestSelection`) —
   * so it can neither name the people nor see across brands, which is the entire question
   * a cross-company queue asks. What makes the identities derivable anyway is that every
   * request carries `boardItemActiveId` (the survivor of any merge, not the raw promotion
   * target) and `listAll` returns the WHOLE queue with no limit: the siblings are already
   * in the array being rendered. Grouping by that id recovers the full cross-company set
   * client-side. Verified `listAll` is unpaginated before relying on it — this would
   * silently under-report the moment the endpoint grew a page size, and the fix then is a
   * server-side aggregate, not a bigger page.
   *
   * Built over ALL `requests`, never `visibleRequests`: how many people asked is a fact
   * about the request, and it must not shrink because a staffer filtered the list.
   *
   * Deduped by user identity, because two requests from the same person are one person
   * asking twice — the count is of PEOPLE, which is what the sentence beside it claims.
   */
  const askersFor = useMemo(() => {
    const byBoardItem = new Map<string, SupportRequest[]>()
    for (const request of requests) {
      if (!request.boardItemActiveId) continue
      const group = byBoardItem.get(request.boardItemActiveId)
      if (group) group.push(request)
      else byBoardItem.set(request.boardItemActiveId, [request])
    }

    const toUser = (request: SupportRequest) => ({
      id: request.userId,
      name: request.submitterName,
      email: request.submitterEmail,
    })

    return (request: SupportRequest) => {
      const siblings = request.boardItemActiveId
        ? (byBoardItem.get(request.boardItemActiveId) ?? [request])
        : [request]

      const seen = new Set<string>()
      const people: ReturnType<typeof toUser>[] = []
      for (const sibling of siblings) {
        // Fall back to the id when there is no email, and to the request's own id when
        // there is neither — an anonymous submitter is still a distinct person, and
        // keying them all on '' would collapse them into one face.
        const key = sibling.submitterEmail ?? sibling.userId ?? sibling.id
        if (seen.has(key)) continue
        seen.add(key)
        people.push(toUser(sibling))
      }
      return people
    }
  }, [requests])

  // Shared with the request's own page, so a status a staffer can set from the
  // queue is one they can also set (and undo) from where the queue sent them.
  const requestActions = useRequestActions()
  const { mutate: setBoardStatus } = useInternalSetBoardStatus()
  const { mutate: mergeItems } = useInternalMergeBoardItems()
  const { mutate: createTheme } = useInternalCreateTheme()
  const { mutate: setItemTheme } = useInternalSetItemTheme()

  const fail = (error: any, fallback: string) =>
    notification({ type: 'error', title: error?.message || fallback })

  /**
   * Themes and company items, split.
   *
   * The split is `isTheme`, sent by the API, NOT a `companyId === null` check
   * written here — see `BoardItemInternal.isTheme`. A theme's title is distilled
   * from several brands' requests, so the one place that decides "is this a theme"
   * is the server.
   *
   * Themes lead, ordered by DISTINCT COMPANIES descending. That ordering is the
   * whole argument for themes existing: raw votes let one 200-seat brand outrank
   * forty companies, so the list a Vera staffer reads top-down has to be ranked by
   * how many CUSTOMERS asked.
   */
  const { themes, companyItems } = useMemo(() => {
    const all = boardItems as BoardItemInternal[]
    return {
      themes: all
        .filter(item => item.isTheme)
        .sort(
          (a, b) =>
            b.distinctCompanyCount - a.distinctCompanyCount ||
            a.title.localeCompare(b.title),
        ),
      companyItems: all.filter(item => !item.isTheme),
    }
  }, [boardItems])

  /**
   * A theme's linked items, keyed by the theme's id.
   *
   * Built from `companyItems` — the same array the list below renders — because the
   * board call this page makes passes no kind or status filter and no limit, so every
   * child is already here carrying its own `themeItemId`. That was verified against
   * `listBoardInternal` before writing this: adding an endpoint for a tree the payload
   * already describes would have been a round trip bought for nothing.
   *
   * Ordered by company, then title. A theme's whole claim is "several BRANDS want
   * this", so grouping a brand's items together is what makes that claim checkable at
   * a glance — an arbitrary creation order makes the reader do the grouping.
   */
  const themeChildren = useMemo(() => {
    const byTheme = new Map<string, BoardItemInternal[]>()
    for (const child of companyItems) {
      if (!child.themeItemId) continue
      const group = byTheme.get(child.themeItemId)
      if (group) group.push(child)
      else byTheme.set(child.themeItemId, [child])
    }
    for (const group of byTheme.values()) {
      group.sort(
        (a, b) =>
          (a.companyName ?? '').localeCompare(b.companyName ?? '') ||
          a.title.localeCompare(b.title),
      )
    }
    return byTheme
  }, [companyItems])

  /** Theme options for the picker, with the distinct-company count on the right. */
  const themeOptions = useMemo(
    () =>
      themes.map(item => ({
        value: item.id,
        label: item.title,
        rightLabel: `${item.distinctCompanyCount} ${
          item.distinctCompanyCount === 1 ? 'company' : 'companies'
        }`,
        icon: <LayersIcon size={16} strokeWidth={1} />,
      })),
    [themes],
  )

  const boardActions = (item: BoardItemInternal) => [
    ...(['planned', 'in_progress', 'shipped'] as const)
      .filter(status => status !== item.status)
      .map(status => ({
        label: `Mark ${BOARD_ITEM_STATUSES[status].label.toLowerCase()}`,
        // Each status names its own verb. All three used to be CircleDotIcon, which
        // meant the icon column — the thing the eye lands on first in a menu — carried
        // no information at all and made three different outcomes look like one
        // repeated row (§21).
        icon: BOARD_STATUS_ICONS[status],
        onClick: () =>
          setBoardStatus(
            { id: item.id, status },
            { onError: e => fail(e, "Couldn't change the status.") },
          ),
      })),
    /*
      GROUPING ACROSS COMPANIES — a LINK, and the separation from Merge below it is
      deliberate rather than a naming choice. Merge is intra-company and destructive
      to the source (votes move, the row is marked `merged`, the submitter is
      notified); Link is cross-company and moves nothing. They sit adjacent because
      a staffer reaching for one is often deciding between them, and the labels
      have to carry the difference on their own.

      Hidden on a theme: a theme cannot be filed under a theme (invariant 1, which
      the API enforces — this only keeps the menu honest).
    */
    {
      label: item.themeItemId ? 'Move to another theme…' : 'Link to a theme…',
      icon: <LayersIcon strokeWidth={1} />,
      divider: true,
      visible: !item.isTheme,
      onClick: () => setThemePicker({ id: item.id, themeItemId: item.themeItemId ?? '' }),
    },
    {
      label: 'Create a theme from this…',
      icon: <PlusIcon strokeWidth={1} />,
      visible: !item.isTheme && !item.themeItemId,
      onClick: () => setNewTheme({ title: item.title, fromItemId: item.id }),
    },
    {
      // X, not a trash can: the item survives, only the grouping is severed (§38).
      label: 'Remove from theme',
      icon: <XIcon strokeWidth={1} />,
      visible: !item.isTheme && Boolean(item.themeItemId),
      onClick: () =>
        setItemTheme(
          { id: item.id, themeItemId: null },
          { onError: e => fail(e, "Couldn't remove this item from its theme.") },
        ),
    },
    {
      label: 'Merge into…',
      icon: <MergeIcon strokeWidth={1} />,
      divider: true,
      // Merging is company-scoped; a theme belongs to no company, so it has no
      // candidates and the row would open an empty picker.
      visible: !item.isTheme,
      onClick: () => setMergeSourceId(item.id),
    },
    {
      label: 'Decline',
      icon: <ThumbsDownIcon strokeWidth={1} />,
      destructive: true,
      onClick: () => setDeclineNote({ id: item.id, note: '' }),
    },
  ]

  const tabButton = (value: 'requests' | 'board', label: string) => (
    <Button
      key={value}
      variant={tab === value ? 'primary' : 'secondary'}
      disableElevation
      onClick={() => setTab(value)}
    >
      {label}
    </Button>
  )

  /*
    Same control shape as `tabButton`, and that is deliberate rather than lazy:
    both answer "which set of rows am I looking at", so making them look different
    would suggest a distinction that is not there. It sits INSIDE the requests tab
    rather than in the header, because it does not apply to Boards — a scope
    selector in the header would be visibly inert half the time.
  */
  const scopeButton = (value: SupportQueueScope, label: string) => (
    <Button
      key={value}
      variant={scope === value ? 'primary' : 'secondary'}
      disableElevation
      onClick={() => setScope(value)}
    >
      {label}
    </Button>
  )

  return (
    /*
      CAPPED TO THE READABLE COLUMN, because this is a card list of prose — not a table.

      The customer-facing twins are already capped: `My requests` and company `Support`
      both carry `narrow: true` in `SettingsPage`'s menu. This page renders the same
      shape (a stack of cards, each a title plus a wrapped description) against the same
      data, so full-bleed made the internal view the odd one out rather than the dense one.

      Width was doing nothing useful here. The rows wrap rather than truncate, so extra
      width only lengthens the line past a comfortable measure, and it pushes each row's
      trailing metadata ("3 votes · Louis Vuitton") far from the title it belongs to —
      the same separation that measured 197px on an uncapped invoice and 12px once capped.

      The cap wraps the WHOLE page, `Header` included. Capping only the content left
      `WhatsNewPage`'s title at the page's left edge while its cards sat centred hundreds
      of pixels away, reading as two unrelated things; a centred column owns its heading.

      Safe to cap because this page contains NO data table — verified before applying.
      Capping a page that embeds one is what put 692px of `RolePage`'s grid behind a
      horizontal scrollbar in simulation.
    */
    <Box sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' }}>
      <Header
        title="Support"
        breadcrumbs={false}
        rightComponents={
          <Stack direction="row" spacing={4}>
            {/* Only on the board tab, because a theme is a board object. Left of
                the tab pair, which is this header's navigation rather than an
                action — §26's "one primary, rightmost" is about ACTION groups,
                and putting a create button to the right of the tabs would read as
                a third tab. */}
            {tab === 'board' && (
              <Button
                variant="secondary"
                disableElevation
                startIcon={<LayersIcon size={16} strokeWidth={1} />}
                onClick={() => setNewTheme({ title: '' })}
              >
                New theme
              </Button>
            )}
            {tabButton('requests', 'Requests')}
            {tabButton('board', 'Boards')}
          </Stack>
        }
      />

      {tab === 'requests' ? (
        <Stack spacing={4}>
          {/* Search / Filter / Sort — the same `ActionLine` every other list surface
              in the app uses, so a staffer arriving from Companies or Orders already
              knows how to narrow this one.

              Rendered only when there is something to narrow. A toolbar over an empty
              queue is furniture: it cannot help, and it pushes the empty state's
              explanation down behind a control there is no reason to touch. Same for
              the error branch, where the list did not arrive at all. */}
          {/*
            Rendered unconditionally, unlike the toolbar below it. An empty
            `brands` queue is exactly when a staffer most needs to be able to ask
            "empty, or filtered?" — hiding the control when there is nothing to
            show would make the default scope invisible at the one moment it is
            doing something surprising. §29: 12px between buttons acting as a group.
          */}
          <Stack direction="row" spacing={3}>
            {scopeButton('brands', 'Brands')}
            {scopeButton('internal', 'Vera')}
            {scopeButton('all', 'All')}
          </Stack>

          {!isLoading && !isError && requests.length > 0 && (
            <InternalSupportListControls
              value={filters}
              onChange={setFilters}
              requests={requests}
            />
          )}

          {isLoading ? (
            [0, 1, 2, 3].map(i => (
              <Card key={i} sx={{ p: 4, mb: 0 }}>
                <Stack spacing={3}>
                  <Stack direction="row" spacing={3}>
                    <Skeleton width={64} height={22} />
                    <Skeleton width={120} height={22} />
                  </Stack>
                  <Skeleton lines={2} />
                </Stack>
              </Card>
            ))
          ) : isError ? (
            /* THE defect this guards against: before internal_support_manage was
               seeded, this rendered "Nothing in the queue" while the API returned
               403 and requests existed. An operator would have concluded there
               was no work, with nothing on screen to suggest otherwise. */
            <QueryErrorState
              error={error}
              subject="the support queue"
              permissionLabel="Manage support"
              onRetry={() => refetch()}
            />
          ) : requests.length === 0 ? (
            <EmptyState
              icon={<LifeBuoyIcon strokeWidth={1} />}
              title="Nothing in the queue"
              text="Requests from every company land here as they come in."
            />
          ) : visibleRequests.length === 0 ? (
            /* "Nothing matches" is a DIFFERENT answer from "nothing exists", and
               conflating them is the bug this avoids: telling a staffer the queue is
               empty when their own filters excluded it reads as data loss. */
            <EmptyState
              icon={<SearchXIcon strokeWidth={1} />}
              title="No requests match your filters"
              text={`None of the ${requests.length} request${
                requests.length === 1 ? '' : 's'
              } in the queue match what you have selected. Try a different search, or clear the filters.`}
              buttons={
                <Button
                  variant="secondary"
                  size="medium"
                  onClick={clearFilters}
                  sx={{ textTransform: 'none' }}
                >
                  Clear filters
                </Button>
              }
            />
          ) : (
            visibleRequests.map((request: SupportRequest) => {
              const open = () =>
                navigate(ROUTES.INTERNAL_SUPPORT_REQUEST(request.id), { state: referrer })
              const askers = askersFor(request)
              // VRG-726 — the queue sorts on last activity by default, so the time shown is
              // that one; "Active" says so when it is not simply when the row was filed.
              const activityAt = lastActivityAt(request)
              const activeSinceCreated =
                Date.parse(activityAt) - Date.parse(request.createdAt) > 60_000
              return (
                <Card
                  key={request.id}
                  role="link"
                  tabIndex={0}
                  onClick={open}
                  // Keyboard parity with the customer list, and for the same reason
                  // it was added there: `Card` renders a plain div, so without this a
                  // keyboard user has no way to open a request at all. preventDefault
                  // so Space activates rather than scrolling the page.
                  onKeyDown={(event: React.KeyboardEvent) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    open()
                  }}
                  sx={{
                    p: 4,
                    mb: 0,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease-in',
                    outline: 'none',
                    // Action row, not a selectable card: clicking navigates rather
                    // than leaving the card visibly selected, so brand border + focus
                    // ring is the right half of §34.
                    '&:hover, &:focus-visible': {
                      borderColor: theme.palette.brand[600],
                      boxShadow: `0 0 0 3px ${alpha(theme.palette.brand[600], 0.06)}`,
                    },
                  }}
                >
                  <Stack spacing={3}>
                    <Stack direction="row" spacing={3} alignItems="center">
                      {/* Type is a fixed CLASSIFICATION, status is a lifecycle STATE.
                          Two pills side by side read as two states of one thing, which
                          is why the brand card splits them into badge + pill — and why
                          this one now does too. It is also the same component the Type
                          facet renders (§36), so the chip you select in the drawer is
                          the chip on the card it selects. */}
                      <SupportTypeBadge type={request.type} />
                      {/* VRG-427: staff need to see "the app filed this" at a glance —
                          a system report competing for triage attention against a
                          paying brand's own words is a different situation entirely. */}
                      {/* The count is NOT passed here (VRG-726): it moved to the triage
                          line below, beside the ref, where it is read with it. Passing it
                          too would print it twice on one card. */}
                      {request.isSystemGenerated && <SystemGeneratedBadge />}
                      <StatusChip
                        statuses={SUPPORT_REQUEST_STATUSES_INTERNAL}
                        status={request.status}
                        size="small"
                      />
                      <Typography variant="body4" color="text.secondary">
                        #{request.reference}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="body4" color="text.secondary">
                        {activeSinceCreated
                          ? `Active ${relativeOrDateTime(activityAt)}`
                          : relativeOrDateTime(request.createdAt)}
                      </Typography>
                      <Box onClick={e => e.stopPropagation()}>
                        <ActionDropdown items={requestActions(request)} />
                      </Box>
                    </Stack>

                    <SourceLine request={request} />

                    <Typography
                      variant="body3"
                      sx={{
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {request.body}
                    </Typography>

                    {/*
                      WHO ASKED, AND FOR WHICH BRAND.

                      The company used to be half of a "#1033 · Louis Vuitton" text run in
                      the header row, and the submitter appeared nowhere at all — so the
                      two questions a cross-company queue is answered by ("who is this",
                      "which brand") were one unstyled string and one missing field. On the
                      customer's own list neither question exists: every row is you, at your
                      company. This is the line that has to carry them.

                      Faces rather than names alone because a queue is scanned, not read.
                      An avatar is recognised before a name is parsed, and the company mark
                      is how a staffer picks their accounts out of a mixed list at a glance.
                    */}
                    <Stack
                      direction="row"
                      spacing={3}
                      alignItems="center"
                      sx={{ flexWrap: 'wrap', rowGap: 2 }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <UserAvatarGroup users={askers} size={24} />
                        <Typography variant="body4" color="text.secondary">
                          {/*
                            "N people asked" only when N people actually did. The count is
                            the number of DISTINCT SUBMITTERS across every request tracked
                            by the same board item — deduped, so one person filing twice is
                            one person, and computed over the whole queue rather than the
                            filtered view, because how many people asked is a fact about the
                            request and must not change when a staffer narrows the list.
                          */}
                          {askers.length > 1
                            ? `${askers.length} people asked for this`
                            : `Raised by ${askers[0]?.name || askers[0]?.email || 'someone unknown'}`}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={2} alignItems="center">
                        {/* The same `CompanyAvatar` the Company filter chip and every
                            company row in the app uses, rather than a second thing that
                            resembles it (§28/§36). Logo is not on the request wire shape,
                            so this renders initials — which is still a stable, per-brand
                            mark that reads at a glance. */}
                        <CompanyAvatar
                          name={request.companyName ?? 'Unknown company'}
                          size={20}
                          radius="4px"
                        />
                        <Typography variant="body4" color="text.secondary">
                          {request.companyName ?? 'Unknown company'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>
              )
            })
          )}
        </Stack>
      ) : (
        <Stack spacing={4}>
          {/*
            CREATE A THEME. Inline at the top of the list rather than in a modal,
            matching the merge and decline affordances already on this page — the
            whole board tab is a curation surface where the context you need is the
            list itself, and a modal would cover the items you are theming.
          */}
          {newTheme && (
            <Card
              sx={{
                p: 4,
                mb: 0,
                borderColor: theme.palette.brand[500],
                backgroundColor: alpha(theme.palette.brand[500], 0.06),
              }}
            >
              <Stack spacing={3}>
                <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  A theme groups the same request from several companies. It belongs to
                  no company, so no customer ever sees it. Linking an item to it moves
                  no votes and changes no statuses.
                </Typography>
                <Input
                  label="What are they all asking for?"
                  placeholder="Bulk certificate export"
                  value={newTheme.title}
                  onValueChange={title => setNewTheme({ ...newTheme, title })}
                />
                <Stack direction="row" spacing={3} justifyContent="flex-end">
                  <Button
                    variant="secondary"
                    disableElevation
                    onClick={() => setNewTheme(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    disableElevation
                    disabled={!newTheme.title.trim()}
                    onClick={() =>
                      createTheme(
                        {
                          title: newTheme.title.trim(),
                          // Set when the theme was started FROM an item, which links
                          // it in the same call — the common case, since you notice a
                          // theme while looking at the second company to ask.
                          fromItemId: newTheme.fromItemId,
                        },
                        {
                          onSuccess: () => {
                            setNewTheme(null)
                            notification({ type: 'success', title: 'Theme created.' })
                          },
                          onError: e => fail(e, "Couldn't create that theme."),
                        },
                      )
                    }
                  >
                    {newTheme.fromItemId ? 'Create and link' : 'Create theme'}
                  </Button>
                </Stack>
              </Stack>
            </Card>
          )}

          {boardLoading ? (
            [0, 1, 2].map(i => (
              <Card key={i} sx={{ p: 4, mb: 0 }}>
                <Stack spacing={2}>
                  <Skeleton width={200} height={20} />
                  <Skeleton lines={2} />
                </Stack>
              </Card>
            ))
          ) : boardError ? (
            <QueryErrorState
              error={boardErrorObj}
              subject="the board queue"
              permissionLabel="Manage support"
              onRetry={() => refetchBoard()}
            />
          ) : boardItems.length === 0 ? (
            <EmptyState
              icon={<LightbulbIcon strokeWidth={1} />}
              title="No board items"
              text="Promote a feature request from the queue and it appears here for curation."
            />
          ) : (
            [...themes, ...companyItems].map((item: BoardItemInternal) => (
              <Card
                key={item.id}
                sx={{
                  p: 4,
                  mb: 0,
                  // A theme is a different KIND of row, not a highlighted one: it
                  // groups other rows rather than being one. A brand tint is what
                  // the app already uses for "this is the grouping/selected thing"
                  // (§34), so the distinction reads without a legend.
                  ...(item.isTheme
                    ? {
                        borderColor: theme.palette.brand[500],
                        backgroundColor: alpha(theme.palette.brand[500], 0.06),
                      }
                    : {}),
                }}
              >
                <Stack spacing={3}>
                  <Stack direction="row" spacing={3} alignItems="center">
                    {item.isTheme ? (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <LayersIcon
                          size={16}
                          strokeWidth={1}
                          color={theme.palette.brand[600]}
                        />
                        <Typography variant="body4" color="text.secondary">
                          Theme
                        </Typography>
                      </Stack>
                    ) : (
                      <StatusChip
                        statuses={BOARD_ITEM_STATUSES}
                        status={item.status}
                        size="small"
                      />
                    )}
                    <Typography variant="body3">{item.title}</Typography>
                    <Box sx={{ flex: 1 }} />
                    {item.isTheme ? (
                      /*
                        THE RANKING FIGURE, and the reason themes exist. Distinct
                        COMPANIES leads; the raw vote total is kept beside it as the
                        secondary figure rather than deleted, because it is still
                        the only measure of intra-company enthusiasm — it is just
                        not the one to sort by, since forty companies asking once
                        beats one 200-seat brand asking forty times.
                      */
                      <Typography variant="body4" color="text.secondary">
                        {item.distinctCompanyCount}{' '}
                        {item.distinctCompanyCount === 1 ? 'company' : 'companies'} ·{' '}
                        {item.linkedItemCount}{' '}
                        {item.linkedItemCount === 1 ? 'request' : 'requests'} ·{' '}
                        {item.voteCount} {item.voteCount === 1 ? 'vote' : 'votes'}
                      </Typography>
                    ) : (
                      /* Staff DO see the raw count — they act on it. The company
                         surface never does. */
                      <Typography variant="body4" color="text.secondary">
                        {item.voteCount} {item.voteCount === 1 ? 'vote' : 'votes'} ·{' '}
                        {item.companyName ?? 'All companies'}
                      </Typography>
                    )}
                    <ActionDropdown items={boardActions(item)} />
                  </Stack>

                  {/*
                    WHAT IS ACTUALLY IN THIS THEME.

                    The grouping used to be visible only from the CHILD's side — a
                    small "Part of …" line on each linked item — so a theme could tell
                    you it covered three companies and eight requests but never which
                    ones. That is backwards for the job the theme exists to do: a
                    staffer ranks themes in order to decide what to build, and "eight
                    requests" is not a thing you can decide anything from. Whether it is
                    three strategic accounts or three trials, and whether the eight are
                    the same ask or three different asks wearing one title, is the whole
                    judgement — and it was one click into a kebab, per row, to recover.

                    NO API CHANGE, checked rather than assumed: `listBoardInternal`
                    applies no kind/status filter and no limit when called the way this
                    page calls it, so every linked child is already in the same payload
                    carrying its own `themeItemId`. Grouping them here recovers the tree.

                    STAFF-ONLY, like the names line it replaces: these are other brands'
                    names and asks. Nothing company-facing renders a theme at all — the
                    API cannot even serve one to a brand (see `listBoard`).
                  */}
                  {item.isTheme &&
                    (themeChildren.get(item.id)?.length ? (
                      <Stack
                        spacing={2}
                        sx={{
                          pl: 3,
                          // A rule rather than a card-in-a-card: these are parts of the
                          // theme, and boxing them would read as five more board items
                          // that happen to sit inside one.
                          borderLeft: `1px solid ${alpha(theme.palette.brand[500], 0.35)}`,
                        }}
                      >
                        {themeChildren.get(item.id)!.map(child => (
                          <Stack
                            key={child.id}
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{ minWidth: 0 }}
                          >
                            {/* Each child keeps its OWN status. A theme has no status of
                                its own, and the useful read is precisely that they can
                                differ — one brand's copy shipped while another's is still
                                under review is exactly the state worth seeing here. */}
                            <StatusChip
                              statuses={BOARD_ITEM_STATUSES}
                              status={child.status}
                              size="small"
                            />
                            <Typography variant="body4" noWrap sx={{ minWidth: 0 }}>
                              {child.title}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            {child.requestCount > 1 && (
                              <Typography variant="body4" color="text.secondary">
                                {child.requestCount} requests
                              </Typography>
                            )}
                            <CompanyAvatar
                              name={child.companyName ?? 'Unknown company'}
                              size={16}
                              radius="3px"
                            />
                            <Typography
                              variant="body4"
                              color="text.secondary"
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {child.companyName ?? 'Unknown company'}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    ) : (
                      item.linkedCompanyNames.length > 0 && (
                        /* Fallback, and deliberately kept: `linkedCompanyNames` is the
                           SERVER's answer to "who is in this theme". If the client-side
                           grouping above ever comes back empty while the server says the
                           theme has members — a filtered board call, a child the payload
                           does not carry — this still names them rather than silently
                           rendering a theme that looks empty. */
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          sx={{ flexWrap: 'wrap', rowGap: 1 }}
                        >
                          <BuildingIcon size={13} strokeWidth={1.5} />
                          <Typography variant="body4" color="text.secondary">
                            {item.linkedCompanyNames.join(' · ')}
                          </Typography>
                        </Stack>
                      )
                    ))}

                  {/* Where a company item's grouping is visible without opening the
                      kebab — otherwise "is this already themed?" costs a click per row. */}
                  {!item.isTheme && item.themeTitle && (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <LayersIcon size={13} strokeWidth={1.5} />
                      <Typography variant="body4" color="text.secondary">
                        Part of “{item.themeTitle}”
                      </Typography>
                    </Stack>
                  )}

                  {item.description && (
                    <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {item.description}
                    </Typography>
                  )}

                  {themePicker?.id === item.id && (
                    <Card sx={{ p: 3, mb: 0 }}>
                      <Stack spacing={3}>
                        <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          Group this under a cross-company theme. Nothing moves. Votes
                          stay where they are, the status does not change, and nobody at
                          the other companies is told.
                        </Typography>
                        {themeOptions.length === 0 ? (
                          <Typography variant="body4" color="text.secondary">
                            There are no themes yet. Use “Create a theme from this” instead.
                          </Typography>
                        ) : (
                          <SelectField
                            label="Theme"
                            search
                            placeholder="Pick a theme"
                            options={themeOptions}
                            value={themePicker.themeItemId}
                            onChange={value =>
                              setThemePicker({ id: item.id, themeItemId: value as string })
                            }
                          />
                        )}
                        <Stack direction="row" spacing={3} justifyContent="flex-end">
                          <Button
                            variant="secondary"
                            disableElevation
                            onClick={() => setThemePicker(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            disableElevation
                            disabled={!themePicker.themeItemId}
                            onClick={() =>
                              setItemTheme(
                                { id: item.id, themeItemId: themePicker.themeItemId },
                                {
                                  onSuccess: () => {
                                    setThemePicker(null)
                                    notification({ type: 'success', title: 'Linked to the theme.' })
                                  },
                                  onError: e => fail(e, "Couldn't link this item to that theme."),
                                },
                              )
                            }
                          >
                            Link
                          </Button>
                        </Stack>
                      </Stack>
                    </Card>
                  )}

                  {mergeSourceId === item.id && (
                    <Card sx={{ p: 3, mb: 0 }}>
                      <Stack spacing={3}>
                        <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          Pick the item this one duplicates. Votes move across, and
                          anyone who voted for both is counted once.
                        </Typography>
                        <Stack spacing={2}>
                          {boardItems
                            .filter(
                              candidate =>
                                candidate.id !== item.id &&
                                candidate.status !== 'merged' &&
                                candidate.companyId === item.companyId,
                            )
                            .map(candidate => (
                              <Button
                                key={candidate.id}
                                variant="secondary"
                                disableElevation
                                onClick={() =>
                                  mergeItems(
                                    { sourceId: item.id, targetItemId: candidate.id },
                                    {
                                      onSuccess: () => {
                                        setMergeSourceId(null)
                                        notification({ type: 'success', title: 'Merged.' })
                                      },
                                      onError: e => fail(e, "Couldn't merge these items."),
                                    },
                                  )
                                }
                              >
                                {candidate.title}
                              </Button>
                            ))}
                        </Stack>
                        <Stack direction="row" spacing={3} justifyContent="flex-end">
                          <Button
                            variant="secondary"
                            disableElevation
                            onClick={() => setMergeSourceId(null)}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Stack>
                    </Card>
                  )}

                  {declineNote?.id === item.id && (
                    <Card sx={{ p: 3, mb: 0 }}>
                      <Stack spacing={3}>
                        {/* The API rejects a decline with no reason. Saying so here
                            rather than letting the user discover it via a 400. */}
                        <Input
                          label="Why are you declining this?"
                          placeholder="The submitter is told, so make it useful."
                          value={declineNote.note}
                          onValueChange={note => setDeclineNote({ id: item.id, note })}
                          multiline
                          minRows={3}
                        />
                        <Stack direction="row" spacing={3} justifyContent="flex-end">
                          <Button
                            variant="secondary"
                            disableElevation
                            onClick={() => setDeclineNote(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            disableElevation
                            disabled={!declineNote.note.trim()}
                            onClick={() =>
                              setBoardStatus(
                                { id: item.id, status: 'declined', note: declineNote.note.trim() },
                                {
                                  onSuccess: () => setDeclineNote(null),
                                  onError: e => fail(e, "Couldn't decline this item."),
                                },
                              )
                            }
                          >
                            Decline and tell them
                          </Button>
                        </Stack>
                      </Stack>
                    </Card>
                  )}
                </Stack>
              </Card>
            ))
          )}
        </Stack>
      )}
    </Box>
  )
}
